import { ProductScreens } from '@euler/app/flows/products/screens';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useTrackNavigator } from '@euler/functions/navigatorTracking';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { useCreation } from '@euler/utils/hooks';
import {
  NavigationContainer,
  NavigationContainerRef,
  StackActions,
} from '@react-navigation/native';
import {
  StackNavigationOptions,
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import {
  FC,
  ReactNode,
  forwardRef,
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { BackHandler } from 'react-native';
import { BehaviorSubject, Subject } from 'rxjs';
import { useTheme } from 'styled-components';
import { BackButton } from './BackButton';
import { CustomDefectiveResultScreen } from './CustomDefectiveResultScreen';
import { CustomIssueScreen } from './CustomIssueScreen';
import {
  NavHeaderTitle,
  NavHeaderTitleForIpadLandscape,
  NavHeaderTitleForIpadPortrait,
} from './NavHeaderTitle';
import { SelectCustomIssueScreen } from './SelectCustomIssueScreen';
import {
  SiteInspectionNavigatorContext,
  useSiteInspectionNavigatorContext,
} from './SiteInspectionContext';
import { SiteInspectionScreen } from './SiteInspectionScreen';
import { DismissEvent, SiteInspectionFlowRef } from './types';

const SiteInspectionScreens = {
  _siteInspection: SiteInspectionScreen,
  _customIssue: CustomIssueScreen,
  _customDefectiveResult: CustomDefectiveResultScreen,
  _selectCustomIssue: SelectCustomIssueScreen,
  ...ProductScreens,
};

export type SiteInspectionNavParams = ScreenToParams<
  typeof SiteInspectionScreens
>;

const Stack = createStackNavigator<SiteInspectionNavParams>();

const useScreenOptions = (): StackNavigationOptions => {
  const { rpx } = useResponsiveContext();
  return {
    ...TransitionPresets.SlideFromRightIOS,
    headerShown: true,
    headerBackTitle: '',
    headerTitleAlign: 'center',
    headerTitleStyle: {
      fontSize: rpx(18),
      fontFamily: FontFamily.NotoSans.Regular,
      fontWeight: '400',
    },
    headerStatusBarHeight: 0,
    headerStyle: {
      height: rpx(44),
      elevation: 0,
      borderBottomWidth: 0,
    },
    headerTitle: NavHeaderTitle,
    headerLeft: () => <BackButton />,
    cardStyle: {
      overflow: 'visible',
    },
  };
};

export const usePresentSiteInspectionNavigateAway = () => {
  const { preventNavigateAway$ } = useSiteInspectionNavigatorContext();
  useEffect(() => {
    preventNavigateAway$.next(true);
    return () => preventNavigateAway$.next(false);
  }, [preventNavigateAway$]);
};

export const useSiteInspectionWillDismissListener = (
  listener: (e: DismissEvent) => void,
) => {
  const { onWillDismiss$ } = useSiteInspectionNavigatorContext();
  useEffect(() => {
    const sub = onWillDismiss$.subscribe(e => {
      listener(e);
    });
    return () => sub.unsubscribe();
  }, [listener, onWillDismiss$]);
};

export const SiteInspectionNavigatorContextProvider: FC<{
  navigatorContainerType?: 'bottomsheet' | 'sidepanel';
  children?: ReactNode;
}> = memo(({ navigatorContainerType = 'bottomsheet', children }) => {
  const siteInspectionFlow = useRef<SiteInspectionFlowRef>(null);
  const dirty$ = useCreation(() => new BehaviorSubject(false), []);
  const preventNavigateAway$ = useCreation(
    () => new BehaviorSubject(false),
    [],
  );
  const onWillDismiss$ = useCreation(() => new Subject<DismissEvent>(), []);
  const bottomComponent$ = useCreation(
    () => new BehaviorSubject<ReactNode | undefined>(undefined),
    [],
  );
  const value = useMemo(
    () => ({
      siteInspectionFlow,
      dirty$,
      bottomComponent$,
      preventNavigateAway$,
      onWillDismiss$,
      navigatorContainerType,
    }),
    [
      dirty$,
      bottomComponent$,
      preventNavigateAway$,
      onWillDismiss$,
      navigatorContainerType,
    ],
  );
  return (
    <SiteInspectionNavigatorContext.Provider
      value={value}
      key={navigatorContainerType}
    >
      {children}
    </SiteInspectionNavigatorContext.Provider>
  );
});

export const SiteInspectionNavigator = memo(
  forwardRef<
    NavigationContainerRef<SiteInspectionNavParams>,
    {
      initialScreen: keyof SiteInspectionNavParams;
      initialParams?: any;
      onDismiss?: () => void;
    }
  >((props, forwardedRef) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const screenOptions = useScreenOptions();

    const isLandscape = useIsLandscape();

    const { navigatorContainerType } = useSiteInspectionNavigatorContext();

    const { ref, onStateChange, onReady } = useTrackNavigator(
      'site-inspection/',
      forwardedRef as any,
    );

    useEffect(() => {
      const onBackPressed = () => {
        ref.current?.dispatch(StackActions.pop());
        return true;
      };
      const listener = BackHandler.addEventListener(
        'hardwareBackPress',
        onBackPressed,
      );
      return () => listener.remove();
    }, [ref]);

    return (
      <LayoutProviderView
        fallbackToChildren
        css={`
          flex: 1;
        `}
      >
        <NavigationContainer
          documentTitle={{ enabled: false }}
          ref={ref}
          onReady={onReady}
          onStateChange={onStateChange}
          independent={true}
          onUnhandledAction={action => {
            if (action.type === 'POP') {
              props.onDismiss?.();
            }
          }}
        >
          <Stack.Navigator
            screenOptions={{
              ...{
                ...screenOptions,
                ...(navigatorContainerType === 'bottomsheet'
                  ? undefined
                  : {
                      headerStyle: {
                        height: rpx(64),
                        elevation: 0,
                        borderBottomWidth: 0,
                      },
                      headerTitle: isLandscape
                        ? NavHeaderTitleForIpadLandscape
                        : NavHeaderTitleForIpadPortrait,
                      headerLeft: () => <BackButton size={rpx(32)} />,
                    }),
              },
              cardStyle: {
                overflow: 'visible',
                backgroundColor: theme.page.background,
              },
            }}
            initialRouteName={props.initialScreen}
          >
            {spreadScreens(
              Stack.Screen,
              SiteInspectionScreens,
              props.initialScreen,
              props.initialParams,
            )}
          </Stack.Navigator>
        </NavigationContainer>
      </LayoutProviderView>
    );
  }),
);
